/**
 * FSM 远期房态 futureRoomStatusList：仅拉数并写入本地缓存（2 分钟内存 + localStorage）
 * 供远期房态后续展示「已占用房量」「可售房量」使用，与现有 getAliasPriceQuantity 数据并存
 */

import {
  getFutureRoomStatusListFromThirdParty,
  type FsmFutureRoomStatusItem,
} from '../../../service/storeRevenueApi';
import { getStoreFromLocalStorage, setStoreSliceInLocalStorage } from './storeRevenueLocalStorage';

const TTL_MS = 2 * 60 * 1000; // 2 分钟

interface CacheEntry {
  data: FsmFutureRoomStatusItem[];
  fetchedAt: number;
}

const memoryCache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<FsmFutureRoomStatusItem[]>>();

function cacheKey(hotelId: number | string): string {
  return `futureRoomStatusList:${String(hotelId)}`;
}

function isValid(entry: CacheEntry): boolean {
  return Date.now() - entry.fetchedAt < TTL_MS;
}

function isFsmList(v: unknown): v is FsmFutureRoomStatusItem[] {
  return Array.isArray(v);
}

/** 从 localStorage 恢复（若有且在 TTL 内）并写入内存缓存 */
function hydrateFromLocalStorage(hotelId: number | string): FsmFutureRoomStatusItem[] | null {
  const stored = getStoreFromLocalStorage(hotelId)?.futureRoomStatusList;
  if (!stored?.data || typeof stored.fetchedAt !== 'number') return null;
  if (Date.now() - stored.fetchedAt > TTL_MS) return null;
  if (!isFsmList(stored.data)) return null;
  memoryCache.set(cacheKey(hotelId), { data: stored.data, fetchedAt: stored.fetchedAt });
  return stored.data;
}

/**
 * 获取远期房态列表：优先内存缓存（2 分钟），其次 localStorage（同 TTL），再直调 FSM futureRoomStatusList 并写缓存
 * 时间范围为「今天」至「今天+6天」（中国时区）
 */
export async function getFutureRoomStatusListWithCache(
  hotelId: number | string
): Promise<FsmFutureRoomStatusItem[]> {
  const key = cacheKey(hotelId);
  const entry = memoryCache.get(key);
  if (entry && isValid(entry)) return entry.data;

  const fromLocal = hydrateFromLocalStorage(hotelId);
  if (fromLocal != null) return fromLocal;

  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<FsmFutureRoomStatusItem[]> => {
    const data = await getFutureRoomStatusListFromThirdParty(hotelId);
    const fetchedAt = Date.now();
    memoryCache.set(key, { data, fetchedAt });
    setStoreSliceInLocalStorage(hotelId, 'futureRoomStatusList', { data, fetchedAt });
    return data;
  })().finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
}

/** 仅从缓存读取（不发起请求），用于图表等消费方 */
export function getFutureRoomStatusListFromCache(
  hotelId: number | string
): FsmFutureRoomStatusItem[] | null {
  const key = cacheKey(hotelId);
  const entry = memoryCache.get(key);
  if (entry && isValid(entry)) return entry.data;
  const fromLocal = hydrateFromLocalStorage(hotelId);
  return fromLocal;
}

export function clearFutureRoomStatusCache(hotelId: number | string): void {
  memoryCache.delete(cacheKey(hotelId));
  inFlight.delete(cacheKey(hotelId));
}
